/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When } from "react-if";
import { Input } from "@nordcloud/gnui";
import {
  CloudProviderType,
  EstateV2Filter,
  Maybe,
  Provider,
} from "~/generated/graphql";
import { getFirstItem, isEmpty } from "~/tools";
import { CloudResource } from "~/views/estate/types";
import { AccountSelector } from "./AccountSelector";
import { CloudResourceKeys } from "./CloudResourceSelector";
import { CloudResourcesMultiSelector } from "./CloudResourcesMultiSelector";

type Props = Omit<React.HTMLAttributes<HTMLInputElement>, "onChange"> & {
  small?: boolean;
  cloudResources?: CloudResource[];
  onChange: (resource: CloudResource[]) => void;
  showCreate: boolean;
  onCreate: (id: string, provider?: Maybe<CloudProviderType>) => Promise<void>;
  searchFilters: EstateV2Filter;
  sidebarLabelKey?: CloudResourceKeys;
};

const getCloudProviderType = (provider: Provider) => {
  if (provider === Provider.Openshift) {
    return CloudProviderType.Openshift;
  }
  return CloudProviderType.Kubernetes;
};

export function CloudResourcesMultiInput({
  cloudResources = [],
  onChange,
  placeholder = "Select Resources",
  small,
  searchFilters,
  sidebarLabelKey = "name",
  onCreate,
  showCreate,
  ...rest
}: Props) {
  const [isResourceOpen, setIsResourceOpen] = React.useState(false);
  const [isAccountOpen, setIsAccountOpen] = React.useState(false);

  const [selectedAccountID, setSelectedAccountID] = React.useState<string>(
    () => {
      const [accountId, ...restAccounts] = new Set(
        cloudResources?.map(({ cloudAccountId }) => cloudAccountId ?? "")
      );

      return isEmpty(restAccounts) ? accountId : "";
    }
  );

  const [selectedAccountProvider, setSelectedAccountProvider] = React.useState<
    Maybe<CloudProviderType> | undefined
  >(() => {
    const [accountProvider] = new Set(
      cloudResources?.map(({ provider }) => provider)
    );

    return getCloudProviderType(accountProvider);
  });

  const onApply = (resources: CloudResource[]) => {
    onChange(resources);
    setIsAccountOpen(false);
    setIsResourceOpen(false);
  };

  const onInputClick = () => {
    if (selectedAccountID) {
      setIsResourceOpen(true);
    } else {
      setIsAccountOpen(true);
    }
  };

  const selectedItemsLength = cloudResources?.length ?? 0;

  const displayValue =
    selectedItemsLength > 1
      ? `${selectedItemsLength} Resources Selected`
      : getFirstItem(cloudResources)?.[sidebarLabelKey]?.toString() ?? "";

  return (
    <>
      <Input
        readOnly
        small={small}
        type="text"
        value={displayValue}
        placeholder={placeholder}
        icon="sidebar"
        onClick={onInputClick}
        {...rest}
      />
      <When condition={isAccountOpen}>
        <AccountSelector
          title="Select Kubernetes / OpenShift Account"
          isOpen={isAccountOpen}
          onClose={() => {
            setIsAccountOpen(false);
            setSelectedAccountID("");
            setSelectedAccountProvider(undefined);
          }}
          onSubmit={(id, provider) => {
            setSelectedAccountID(id);
            setSelectedAccountProvider(provider);
            setIsResourceOpen(true);
          }}
        />
      </When>
      <When condition={isResourceOpen}>
        <CloudResourcesMultiSelector
          name="Estate Record"
          isOpen={isResourceOpen}
          showCreate={showCreate}
          options={cloudResources}
          searchFilters={{
            ...(selectedAccountID && { account: [selectedAccountID] }),
            ...searchFilters,
          }}
          onClose={() => setIsResourceOpen(false)}
          onCreate={() => onCreate(selectedAccountID, selectedAccountProvider)}
          onSubmit={onApply}
        />
      </When>
    </>
  );
}
